import { useCallback, useEffect, useState } from 'react';
import type { SessionResponse } from '@kratispot/shared';
import { ApiError, fetchSession } from '@/lib/api';

export type SessionState =
  | { phase: 'checking' }
  | { phase: 'signed-in'; session: SessionResponse }
  | { phase: 'signed-out' }
  | { phase: 'offline' }
  | { phase: 'unreachable' };

// A device with no network and a server that is down are not the same thing to
// the person holding the phone, and only one of them is worth retrying now.
export function failedPhase(online: boolean): 'offline' | 'unreachable' {
  return online ? 'unreachable' : 'offline';
}

export function useSession(): { state: SessionState; reload: () => void } {
  const [state, setState] = useState<SessionState>({ phase: 'checking' });
  const [attempt, setAttempt] = useState(0);
  const reload = useCallback(() => setAttempt((n) => n + 1), []);

  useEffect(() => {
    const controller = new AbortController();
    fetchSession(controller.signal)
      .then((session) => setState({ phase: 'signed-in', session }))
      .catch((error: unknown) => {
        if (controller.signal.aborted) return;
        const out = error instanceof ApiError && error.status === 401;
        setState({ phase: out ? 'signed-out' : failedPhase(navigator.onLine) });
      });
    return () => controller.abort();
  }, [attempt]);

  // Coming back into signal is the retry nobody should have to ask for.
  useEffect(() => {
    window.addEventListener('online', reload);
    return () => window.removeEventListener('online', reload);
  }, [reload]);

  return { state, reload };
}
